import {Button} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {useState} from 'react';
import type {
  AttachmentModuleEnum,
  AttachmentSimpleResponse,
} from 'src/api/ProactApiClient';
import {ProfileAvatar} from 'src/app/personnel/ProfileAvatar';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {AdminRoleGuard} from 'src/app/auth/AdminRoleGuard';
import {useDeleteAttachmentMutation} from 'src/redux/queries/attachment';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {AttachmentForm} from './AttachmentForm';

export interface AttachmentsListRowProps {
  entityId: number;
  attachmentType: AttachmentModuleEnum;
  data: AttachmentSimpleResponse;
  onDeleted: () => void;
  onEdited: () => void;
}
export const AttachmentsListRow = ({
  entityId,
  attachmentType,
  data,
  onDeleted,
  onEdited,
}: AttachmentsListRowProps) => {
  const {setAlert} = useAlert();
  const [deleteAttachment, {isLoading: isDeleting}] =
    useDeleteAttachmentMutation();
  const [showEdit, setShowEdit] = useState(false);
  const [
    confirmationModalOpened,
    {open: openConfirmationModal, close: closeConfirmationModal},
  ] = useDisclosure(false);

  function handleEditClick(): void {
    setShowEdit(true);
  }

  const handleDeleteClick = async () => {
    try {
      await deleteAttachment({id: data.id, entityId}).unwrap();
      onDeleted();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  return (
    <div>
      <Group>
        <ProfileAvatar src={data.creatorAvatarUrl} alt={data.creatorName} />
        <div>
          <Text size="sm">{data.creatorName}</Text>

          <Text size="xs" c="dimmed">
            {data.createdAt.toString().toLongDate()}
          </Text>
        </div>
      </Group>
      <Text pl="xl" pt="sm" size="sm">
        <AttachmentForm
          mode={showEdit ? 'edit' : 'view'}
          data={data}
          entityId={entityId}
          attachmentType={attachmentType}
          onSaved={onEdited}
          onCancelled={() => setShowEdit(false)}
        />
      </Text>
      {!showEdit && (
        <AdminRoleGuard>
          <Group pl="md" gap={1}>
            {data.isEditable ? (
              <>
                <Button
                  variant="subtle"
                  color="black"
                  size="xs"
                  onClick={handleEditClick}>
                  Edit
                </Button>
                <Button
                  variant="subtle"
                  color="black"
                  size="xs"
                  loading={isDeleting}
                  onClick={openConfirmationModal}>
                  Delete
                </Button>
                <ConfirmDialog
                  body="Are you sure you want to delete this attachment?"
                  confirmationButtonColor="red"
                  opened={confirmationModalOpened}
                  onClose={closeConfirmationModal}
                  onConfirm={handleDeleteClick}
                />
              </>
            ) : (
              <Text size="xs" c="dimmed">
                System generated attachments cannot be modified.
              </Text>
            )}
          </Group>
        </AdminRoleGuard>
      )}
    </div>
  );
};
